import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMyRFQs } from '@steelbuy/data-access';
import { RFQBuyerModel, TranslateFunc } from '@steelbuy/ts-shared';
import { getQuoteStatusBadgeVariant } from '@steelbuy/ui-domain';
import {
    BadgeState,
    ButtonTertiaryOnLightM,
    DataHeader,
    DataTable,
    DataTableColumnConfig,
    Icon,
    IconIdentifier,
    InputTextfield,
    LoadingSpinner,
    LoadingStatus,
} from '@steelbuy/ui-primitive';

import { myRfqsTableHeading, quoteTableConfig } from './requestQuoteTableUtils';
import { createRouteUrl, RoutePath } from '../../router/Routes';
import { MyRequestQuotesFilter } from '../../views/request-quote/MyRequestQuotesFilter';
import { useMyRFQsContext } from '../my-rfqs/MyRFQsContext';
import { MyRFQsEmptyState } from '../my-rfqs/MyRFQsEmptyState';

import './MyRequestQuotesTable.scss';

const MY_RFQS_PAGE_SIZE = 15;

const DEBOUNCE_DELAY_MS = 400;

export const MyRequestQuotesTable = () => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const navigate = useNavigate();
    const { myRFQFilter, referenceFilter, setReferenceFilter } = useMyRFQsContext();

    const [inputValue, setInputValue] = useState(referenceFilter);

    useEffect(() => {
        const timer = setTimeout(() => {
            setReferenceFilter(inputValue);
        }, DEBOUNCE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [inputValue, setReferenceFilter]);

    const statusFilter = myRFQFilter.length ? { 'status.value': myRFQFilter.join('|') } : undefined;
    const { isLoading, total, hasNextPage, isFetchingNextPage, fetchNextPage, data } = useMyRFQs({
        pageSize: MY_RFQS_PAGE_SIZE,
        filterCriteria: statusFilter,
        referenceNumber: referenceFilter,
    });
    const columns: DataTableColumnConfig<RFQBuyerModel>[] = myRfqsTableHeading.map((heading) => {
        const config = quoteTableConfig[heading];

        if (heading === 'rfqStatus') {
            return {
                key: heading,
                heading: config?.getHeading(t as TranslateFunc) ?? '',
                renderCell: (quote) => (
                    <BadgeState
                        label={config?.mapValue?.(quote, t as TranslateFunc) as string}
                        variant={getQuoteStatusBadgeVariant(quote.status.value)}
                    />
                ),
            };
        }

        if (heading === 'materialSpecs') {
            return {
                key: heading,
                heading: config?.getHeading(t as TranslateFunc) ?? '',
                renderCell: (quote) =>
                    (config?.mapValue?.(quote, t as TranslateFunc) as string[])?.map((text) => (
                        <div key={text}>{text}</div>
                    )),
            };
        }

        return {
            key: heading,
            heading: config?.getHeading(t as TranslateFunc) ?? '',
            value: (quote) => config?.mapValue?.(quote, t as TranslateFunc) as string,
        };
    });

    return (
        <div className="quotes">
            <div className={classNames('quotes-table', { 'quotes-table--loading': isLoading })}>
                <DataHeader
                    headingValue={
                        isLoading
                            ? ''
                            : t('application.myRFQ.numberOfRFQListings', {
                                  count: total ?? 0,
                              })
                    }
                    searchComponent={
                        <InputTextfield
                            name="rfqReferenceNumber"
                            onChange={setInputValue}
                            value={inputValue}
                            endAdornment={
                                inputValue ? (
                                    <button
                                        type="button"
                                        className="quotes-search-clear-btn"
                                        onClick={() => setInputValue('')}
                                        aria-label={t('application.myRFQ.clearSearch')}
                                    >
                                        <Icon name={IconIdentifier.CLOSE} />
                                    </button>
                                ) : (
                                    <Icon name={IconIdentifier.SEARCH} />
                                )
                            }
                            placeholder={t('application.myRFQ.searchPlaceHolder')}
                        />
                    }
                >
                    <MyRequestQuotesFilter />
                </DataHeader>
                {isLoading && <LoadingSpinner fillContainerHeight />}
                {total && total > 0 ? (
                    <DataTable
                        columns={columns}
                        rows={data ?? []}
                        getRowKey={(quote) => quote.id}
                        onRowClick={(quote) =>
                            navigate(createRouteUrl(RoutePath.MY_REQUEST_QUOTE_DETAILS, ['rfqId', quote.id]))
                        }
                    />
                ) : null}
                {hasNextPage && (
                    <div className="quotes-table__load-more">
                        <ButtonTertiaryOnLightM
                            onClick={() => fetchNextPage()}
                            label={t('uiDomain:commonList.loadMore')}
                            loadingStatus={isFetchingNextPage ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                        />
                    </div>
                )}
                {data?.length === 0 && <MyRFQsEmptyState />}
            </div>
        </div>
    );
};
